import * as React from 'react';
import { Redirect } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import noop from 'lodash/noop';
import { useSession, useAuthentication, useValidateUser } from 'hooks';
import styled from 'styled-components';
import { MOBILE_RESOLUTION } from 'styles/sharedStyle';
import rtl from 'styled-components-rtl';
import BackLink from 'components/BackLink';
import Button from 'components/Button';
import * as Form from 'components/forms';
import { Message } from 'i18n';
import { forgotPasswordSchema } from 'utils/validation.utils';
import { ForgotPasswordPayload } from 'store/types/authentication.types';
import { AuthRoute, ClientRouter } from 'router/routes';
import { AuthForm, Fields } from '../Auth.config';

const ForgotPassword = () => {
  const intl = useIntl();
  const { isAuthenticated } = useValidateUser();
  const { requestResetLink, loading: isLoading } = useAuthentication();
  const methods = useForm<ForgotPasswordPayload>({
    resolver: yupResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
    mode: 'all',
  });
  const { handleSubmit, formState } = methods;
  const { isValid } = formState;
  const { getLocation, country } = useSession();

  const onSubmit = (payload: ForgotPasswordPayload) =>
    requestResetLink(payload);

  React.useEffect(() => {
    if (!country) getLocation();
  }, [country, getLocation]);

  if (isAuthenticated) return <Redirect to={ClientRouter.APP} />;

  return (
    <Container
      title={intl.formatMessage({ id: Message.AUTH_FORGOT_PASSWORD_TITLE })}
      info={intl.formatMessage(
        { id: Message.AUTH_FORGOT_PASSWORD_INFO },
        { linebreak: <br /> }
      )}
    >
      <FormProvider {...methods}>
        <CustomForm onSubmit={handleSubmit(onSubmit)}>
          <Fields className="register-fields" direction="row">
            <Form.Field
              showValidAdornment
              onKeyDown={noop}
              label={{
                title: intl.formatMessage({
                  id: Message.AUTH_LOGIN_EMAIL_FIELD,
                }),
                isBold: true,
              }}
              name="email"
              type="email"
              width="100%"
              placeholder={intl.formatMessage({
                id: Message.AUTH_LOGIN_EMAIL_FIELD_PLACEHOLDER,
              })}
            />
          </Fields>
          <Button
            className="main-button"
            variant="contained"
            color="inherit"
            type="submit"
            disabled={!isValid || isLoading}
            isLoading={isLoading}
          >
            <FormattedMessage id={Message.AUTH_FORGOT_PASSWORD_BUTTON_LABEL} />
          </Button>
          <BackLink
            to={AuthRoute.LOGIN}
            label={intl.formatMessage({
              id: Message.AUTH_FORGOT_PASSWORD_BACK_LINK,
            })}
          />
        </CustomForm>
      </FormProvider>
    </Container>
  );
};

export default ForgotPassword;

const Container = styled(Form.Container).attrs({
  className: 'register-form-container',
})`
  && .auth-form-content {
    ${rtl`
      left: 0;
    `}
    height: 100vh;
    max-height: unset;

    @media (max-width: 1500px) {
      max-width: 535px;
      padding: 23px 40px;
    }

    @media (min-width: 1500px) {
      width: 623px;
      padding: 43px 58px;
    }
    @media ${MOBILE_RESOLUTION} {
      width: 100%;
      padding: 40px 50px;
    }
  }

  && .auth-form-header {
    margin-bottom: 29px;
    flex-direction: row;
    align-items: flex-end;
    gap: 100px;

    @media ${MOBILE_RESOLUTION} {
      margin-bottom: 29px;
      gap: 50px;
    }

    @media (min-width: 1500px) {
      margin-bottom: 29px;
      gap: 95px;
    }

    & .logo {
      @media (max-width: 500px) {
        align-self: center;
        height: 40px;
      }
      align-self: flex-start;
      @media (max-width: 1500px) {
        height: 50px;
      }
      height: 77px;
    }
  }
  && .auth-header-title {
    font-size: 1.25rem;
    ${rtl`
       margin-left:25px;
    `}
    font-weight: bold;
    @media ${MOBILE_RESOLUTION} {
      font-size: 1.05rem;
      font-weight: bold;
      margin-left: 0;
    }
    @media (min-width: 1500px) {
      font-size: 1.75rem;
    }
  }

  && .main-button {
    width: 100%;
    min-height: 48px;
    text-transform: none;
    margin-top: 75px;
  }

  && .MuiFormControl-root {
    @media (min-width: 500px) {
      width: 218px;
    }

    @media (min-width: 1500px) {
      width: 270px;
    }
  }
  & .register-fields {
    margin-top: 30px;
    row-gap: 14px;
    column-gap: 22px;

    @media (max-width: 1500px) {
      gap: 19px;
    }
    @media (max-width: 500px) {
      gap: 28px;
    }
    @media ${MOBILE_RESOLUTION} {
      width: 100%;
    }
  }
`;

const CustomForm = styled(AuthForm)`
  width: 445px;
  @media ${MOBILE_RESOLUTION} {
    width: 100%;
  }
  .back-link {
    margin-top: 41px;
  }
`;
